import React from 'react';
import { motion } from 'framer-motion';

/**
 * Animated Lucide icons for the Netflix player skin.
 *
 * Default sizes are normalised by MEASURED INK HEIGHT, not by the nominal
 * `size`: Lucide's gauge draws inside 15 of its 24 units and the speaker 14.6,
 * where pause/skip/fullscreen fill 18 — so equal `size` values rendered
 * visibly unequal glyphs sitting off the row's centre line. At the values
 * below every glyph in the control bar renders 28px tall on one centre line,
 * with stroke widths trimmed to keep the weight even.
 *
 * The glyphs are Lucide's own path data (lucide-react 1.27), redrawn with
 * `motion` elements so the STROKES animate rather than the whole icon being
 * transformed — the difference between a button that scales and an icon that
 * actually moves the way the control behaves.
 *
 * They carry no hover state of their own. Each is rendered inside a
 * `motion.button` that declares `whileHover="hover"` / `whileTap="tap"`, and
 * framer-motion propagates that variant name down to every motion child, so
 * hovering anywhere on the button drives every stroke in the icon. Wrap the
 * tree in <MotionConfig reducedMotion="user"> and the OS setting is honoured.
 */

const SVG = ({ size, strokeWidth, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const spring = { type: 'spring', stiffness: 320, damping: 18 };
const ease = { duration: 0.32, ease: [0.22, 1, 0.36, 1] };

// Rotating about a point in the 24×24 grid rather than the element's own box.
const originStyle = (x, y) => ({ transformBox: 'view-box', transformOrigin: `${x}px ${y}px` });

// ── Play: the wedge leans into the direction it plays ──────────────────
export const NfxPlay = ({ size = 32, strokeWidth = 1.5 }) => (
  <SVG size={size} strokeWidth={strokeWidth}>
    <motion.path
      d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"
      fill="currentColor"
      style={originStyle(12, 12)}
      variants={{
        hover: { x: 1.5, scale: 1.08 },
        tap: { scale: 0.9 },
      }}
      transition={spring}
    />
  </SVG>
);

// ── Pause: the two bars part, then squeeze on the press ────────────────
export const NfxPause = ({ size = 32, strokeWidth = 1.5 }) => (
  <SVG size={size} strokeWidth={strokeWidth}>
    <motion.rect
      x="5"
      y="3"
      width="5"
      height="18"
      rx="1"
      fill="currentColor"
      variants={{ hover: { x: -1.4, scaleY: 1.06 }, tap: { x: 0, scaleY: 0.9 } }}
      style={originStyle(12, 12)}
      transition={spring}
    />
    <motion.rect
      x="14"
      y="3"
      width="5"
      height="18"
      rx="1"
      fill="currentColor"
      variants={{ hover: { x: 1.4, scaleY: 1.06 }, tap: { x: 0, scaleY: 0.9 } }}
      style={originStyle(12, 12)}
      transition={spring}
    />
  </SVG>
);

// ── Skip dials: the arc winds the way it seeks and the tick follows ────
const skipVariants = (dir) => ({
  arc: {
    hover: { rotate: dir * 40 },
    tap: { rotate: dir * 360 },
  },
  tick: {
    hover: { rotate: dir * 40, scale: 1.12 },
    tap: { rotate: dir * 360 },
  },
});

export const NfxRewind = ({ size = 32, strokeWidth = 2 }) => {
  const v = skipVariants(-1);
  return (
    <SVG size={size} strokeWidth={strokeWidth}>
      <motion.g style={originStyle(12, 12)} variants={v.arc} transition={ease}>
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <motion.path d="M3 3v5h5" variants={v.tick} style={originStyle(5.5, 5.5)} transition={ease} />
      </motion.g>
    </SVG>
  );
};

export const NfxForward = ({ size = 32, strokeWidth = 2 }) => {
  const v = skipVariants(1);
  return (
    <SVG size={size} strokeWidth={strokeWidth}>
      <motion.g style={originStyle(12, 12)} variants={v.arc} transition={ease}>
        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
        <motion.path d="M21 3v5h-5" variants={v.tick} style={originStyle(18.5, 5.5)} transition={ease} />
      </motion.g>
    </SVG>
  );
};

// ── Volume: the waves ripple outward; muted draws its cross in ────────
const SPEAKER = 'M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z';

export const NfxVolume = ({ size = 36, strokeWidth = 1.8, muted = false, level = 1 }) => {
  const silent = muted || level === 0;
  const quiet = !silent && level < 0.5;
  return (
    <SVG size={size} strokeWidth={strokeWidth}>
      <motion.path
        d={SPEAKER}
        variants={{ hover: { scale: 1.06 }, tap: { scale: 0.92 } }}
        style={originStyle(7, 12)}
        transition={spring}
      />
      {silent ? (
        <>
          <motion.line
            x1="22"
            x2="16"
            y1="9"
            y2="15"
            initial={{ pathLength: 1 }}
            variants={{ hover: { pathLength: [0, 1] }, tap: { scale: 0.9 } }}
            transition={{ duration: 0.3 }}
          />
          <motion.line
            x1="16"
            x2="22"
            y1="9"
            y2="15"
            initial={{ pathLength: 1 }}
            variants={{ hover: { pathLength: [0, 1] }, tap: { scale: 0.9 } }}
            transition={{ duration: 0.3, delay: 0.06 }}
          />
        </>
      ) : (
        <>
          <motion.path
            d="M16 9a5 5 0 0 1 0 6"
            variants={{ hover: { x: [0, 1.6, 0], opacity: [1, 0.45, 1] }, tap: { x: 1 } }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          />
          {!quiet ? (
            <motion.path
              d="M19.364 18.364a9 9 0 0 0 0-12.728"
              variants={{ hover: { x: [0, 2.2, 0], opacity: [1, 0.35, 1] }, tap: { x: 1.4 } }}
              transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.07 }}
            />
          ) : null}
        </>
      )}
    </SVG>
  );
};

// ── Fullscreen: the four corners push apart (or fold in) ──────────────
// Four corner marks around a hollow centre read smaller than the solid glyphs
// beside them even at an identical bounding box, so this one is drawn a size
// up (35 against their 32-34) to sit level by eye rather than by measurement.
// The corner push is kept modest for the same reason — at ±1.5 the hovered
// icon grew past its neighbours, which is what made the resting state look
// like the odd one out.
const CORNERS = {
  enter: [
    { d: 'M8 3H5a2 2 0 0 0-2 2v3', dx: -1, dy: -1 },
    { d: 'M21 8V5a2 2 0 0 0-2-2h-3', dx: 1, dy: -1 },
    { d: 'M3 16v3a2 2 0 0 0 2 2h3', dx: -1, dy: 1 },
    { d: 'M16 21h3a2 2 0 0 0 2-2v-3', dx: 1, dy: 1 },
  ],
  exit: [
    { d: 'M8 3v3a2 2 0 0 1-2 2H3', dx: 1, dy: 1 },
    { d: 'M21 8h-3a2 2 0 0 1-2-2V3', dx: -1, dy: 1 },
    { d: 'M3 16h3a2 2 0 0 1 2 2v3', dx: 1, dy: -1 },
    { d: 'M16 21v-3a2 2 0 0 1 2-2h3', dx: -1, dy: -1 },
  ],
};

export const NfxFullscreen = ({ size = 35, strokeWidth = 1.85, active = false }) => (
  <SVG size={size} strokeWidth={strokeWidth}>
    {(active ? CORNERS.exit : CORNERS.enter).map((c) => (
      <motion.path
        key={c.d}
        d={c.d}
        variants={{ hover: { x: c.dx, y: c.dy }, tap: { x: 0, y: 0 } }}
        transition={spring}
      />
    ))}
  </SVG>
);

// ── Speed: the needle sweeps the dial ─────────────────────────────────
export const NfxSpeed = ({ size = 39, strokeWidth = 1.8 }) => (
  <SVG size={size} strokeWidth={strokeWidth}>
    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    <motion.path
      d="m12 14 4-4"
      style={originStyle(12, 14)}
      variants={{ hover: { rotate: [0, -55, 12, 0] }, tap: { rotate: -55 } }}
      transition={{ duration: 0.7, ease: 'easeInOut' }}
    />
  </SVG>
);

// ── Back: the head leads, the shaft draws itself back in ──────────────
export const NfxBack = ({ size = 36, strokeWidth = 2 }) => (
  <SVG size={size} strokeWidth={strokeWidth}>
    <motion.path
      d="m12 19-7-7 7-7"
      variants={{ hover: { x: -2.5 }, tap: { x: -4 } }}
      transition={spring}
    />
    <motion.path
      d="M19 12H5"
      initial={{ pathLength: 1 }}
      variants={{ hover: { pathLength: [0.4, 1] }, tap: { pathLength: 0.6 } }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    />
  </SVG>
);
